import { cva, type VariantProps } from 'class-variance-authority';
import { motion } from 'motion/react';
import React, { type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { Tooltip, type TooltipProps } from './Tooltip';

const buttonVariants = cva(
  'grid place-items-center [grid-template-areas:"stack"] disabled:opacity-50 rounded smoothing-default whitespace-nowrap text-foreground hover:enabled:text-foreground data-[active=true]:text-foreground data-[state=open]:text-foreground disabled:cursor-not-allowed transition-all will-change-[opacity]',
  {
    variants: {
      variant: {
        default:
          'shell-accent bg-accent hover:enabled:bg-accent-hover text-accent-foreground-subtle hover:enabled:text-accent-foreground shadow-sm',
        destructive:
          'bg-danger-9 hover:enabled:bg-danger-10 text-white hover:enabled:text-white shadow-sm',
        'destructive-ghost':
          'text-danger-11 hover:enabled:text-danger-11 hover:enabled:bg-danger-3',
        outline:
          'border border-border-strong hover:enabled:border-border-strongest shadow-sm',
        secondary: 'bg-secondary hover:enabled:bg-secondary-hover shadow-sm',
        ghost:
          'hover:bg-secondary-hover data-[active=true]:bg-secondary-active data-[state=open]:bg-secondary-active disabled:hover:bg-transparent disabled:text-foreground-subtle disabled:hover:text-foreground-subtle',
        translucent:
          'hover:bg-grayA-2 data-[active=true]:bg-grayA-4 data-[state=open]:bg-secondary-active disabled:hover:bg-transparent disabled:text-foreground-subtle disabled:hover:text-foreground-subtle data-[active=false]:text-foreground-subtle data-[active=false]:hover:text-foreground-subtle',
        warning:
          'bg-warning-9 hover:enabled:bg-warning-10 px-4 text-warning-1 hover:enabled:text-warning-1',
        link: 'text-foreground underline-offset-4 hover:enabled:underline',
      },
      size: {
        default: 'h-9 px-4 rounded-lg py-2',
        sm: 'h-8 rounded-lg px-3',
        lg: 'h-10 rounded-lg px-8',
        xs: 'h-6 rounded-md px-2 py-0',
        dense: 'h-6 gap-2 rounded-md px-2 py-0',
        icon: 'h-9 w-9 p-1 [&_svg]:size-3.5 rounded-md',
        'icon-sm': 'h-6 w-6 p-1 [&>svg]:size-3 rounded-md',
      },
      case: {
        uppercase: 'uppercase',
        normal: '',
      },
      font: {
        mono: 'font-mono',
        sans: 'font-sans',
        serif: 'font-serif',
      },
      textSize: {
        xs: 'text-xs leading-none',
        sm: 'text-sm leading-none',
        md: 'text-md leading-none',
        base: 'text-base leading-none',
        lg: 'text-lg leading-none',
      },
      iconSize: {
        xs: '[&_svg]:size-3',
        base: '[&_svg]:size-3.5',
        sm: '[&_svg]:size-4',
        md: '[&_svg]:size-5',
        lg: '[&_svg]:size-6',
      },
      iconOnly: {
        true: '',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      case: 'normal',
      font: 'sans',
      textSize: 'sm',
      iconSize: 'base',
      iconOnly: false,
    },
    compoundVariants: [
      {
        size: 'dense',
        className: 'text-xs tracking-wide',
      },
      {
        size: 'xs',
        iconOnly: true,
        className: 'w-6 px-0',
      },
      {
        size: 'dense',
        iconOnly: true,
        className: 'w-6 px-0',
      },
      {
        size: 'sm',
        iconOnly: true,
        className: 'w-8 px-0',
      },
      {
        size: 'default',
        iconOnly: true,
        className: 'w-9 px-0',
      },
      {
        size: 'lg',
        iconOnly: true,
        className: 'w-10 px-0',
      },
    ],
  }
);

const Loader = () => (
  <Icons.loader className="text-inherit animate-spin size-3" />
);

interface BaseButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  loadingText?: string;
  spinnerPlacement?: 'start' | 'end';
}

const BaseButton = React.forwardRef<HTMLButtonElement, BaseButtonProps>(
  (
    {
      className,
      children,
      variant,
      size,
      textSize,
      isLoading = false,
      loadingText,
      spinnerPlacement = 'start',
      iconSize,
      iconOnly,
      disabled,
      type = 'button',
      ...props
    },
    ref
  ) => {
    return (
      <button
        className={cn(
          buttonVariants({
            size,
            textSize,
            variant,
            iconSize,
            iconOnly,
          }),
          className
        )}
        aria-busy={isLoading}
        disabled={isLoading || disabled}
        type={type}
        ref={ref}
        {...props}
      >
        <span
          className={cn(
            '[grid-area:stack]',
            'flex min-w-0 gap-2 items-center justify-center overflow-hidden leading-normal',
            isLoading && 'invisible'
          )}
        >
          {children}
        </span>
        <span
          className={cn(
            '[grid-area:stack]',
            'flex min-w-0 gap-2 items-center justify-center overflow-hidden leading-normal',
            // Hidden, the spinner only holds the button's width, so it need not spin
            !isLoading && 'invisible [&_svg]:[animation-play-state:paused]'
          )}
          role="status"
          aria-live="polite"
          aria-hidden={!isLoading}
          aria-label={(loadingText ?? '').trim() || 'Loading'}
        >
          {spinnerPlacement === 'start' && <Loader />}
          {loadingText && <p>{loadingText}</p>}
          {spinnerPlacement === 'end' && <Loader />}
        </span>
      </button>
    );
  }
);
BaseButton.displayName = 'BaseButton';

export interface ButtonProps extends BaseButtonProps {
  tooltip?: string;
  tooltipOptions?: Omit<TooltipProps, 'label' | 'children'>;
  /**
   * CSS class for element identification/tagging only, not for styling.
   * This is an opinionated button component with predefined styles via variants.
   * For custom styling, use a headless component instead.
   */
  className?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ tooltip, tooltipOptions, ...props }, ref) => {
    const button = <BaseButton ref={ref} {...props} />;

    if (tooltip)
      return (
        <Tooltip label={tooltip} {...tooltipOptions}>
          {button}
        </Tooltip>
      );

    return button;
  }
);
Button.displayName = 'Button';

interface PressableProps {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  hoverScale?: number;
}

export const Tap = ({
  children,
  className,
  disabled = false,
  hoverScale,
}: PressableProps) => (
  <motion.div
    className={className}
    tabIndex={-1}
    whileHover={!disabled && hoverScale ? { scale: hoverScale } : undefined}
    whileTap={!disabled ? { scale: 0.95 } : undefined}
  >
    {children}
  </motion.div>
);
